/* The recommendation is derived from the rendered product guide, never a generic size chart. */
(() => {
  const normalize = value => String(value).trim().toUpperCase().replace(/\s*\/\s*/g, '/');
  const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const engine = {
    parse(text, sizes) {
      const source = text.replace(/[\u2010-\u2015\u2212]/g, '-').replace(/\u00a0/g, ' ').replace(/(\d),(\d)/g, '$1.$2');
      const names = sizes.map(normalize).sort((a, b) => b.length - a.length).map(escapeRegExp).join('|');
      if (!names) return [];
      const headings = [...source.matchAll(new RegExp(`(?:^|[^A-Z0-9/])(${names})\\s*(?:SIZE\\s*)?:\\s*`, 'gi'))];
      const rules = [];
      headings.forEach((match, i) => {
        const segment = source.slice(match.index + match[0].length, headings[i + 1]?.index ?? source.length).trim();
        // Only a leading adult-height range is valid; garment chest/waist/length is not height.
        const height = segment.match(/^(\d{3}(?:\.\d+)?)\s*-\s*(\d{3}(?:\.\d+)?)\s*(\+)?\s*(?:cm\b|(?![\d.]))/i);
        const openHeight = segment.match(/^(?:\+\s*(\d{3})\s*cm|(\d{3})\s*(?:cm\s*\+|\+\s*cm))/i);
        if (!height && !openHeight) return;
        const heightRange = height ? [Number(height[1]), height[3] ? null : Number(height[2])] : [Number(openHeight[1] || openHeight[2]), null];
        if (heightRange[0] < 140 || heightRange[0] > 220 || (heightRange[1] !== null && (heightRange[1] < heightRange[0] || heightRange[1] > 220))) return;
        // Stop before garment dimensions or the next format to avoid picking up unrelated measurements.
        const header = segment.split(/tour de|poitrine|chest|waist|longueur|length|veste|jacket|pantalon|pants|US FORMAT/i)[0];
        const weight = header.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*kg/i);
        const openWeight = header.match(/(?:(\d+(?:\.\d+)?)\s*kg\s*(?:et\s*)?\+|\+\s*(\d+(?:\.\d+)?)\s*kg)/i);
        const weightRange = weight ? [Number(weight[1]), Number(weight[2])] : openWeight ? [Number(openWeight[1] || openWeight[2]), null] : null;
        if (weightRange && (weightRange[0] < 20 || (weightRange[1] !== null && weightRange[1] < weightRange[0]))) return;
        const size = sizes.find(value => normalize(value) === normalize(match[1]));
        if (!rules.some(rule => rule.size === size)) rules.push({ size, height: heightRange, weight: weightRange });
      });
      return rules.sort((a, b) => a.height[0] - b.height[0] || (a.weight?.[0] ?? 0) - (b.weight?.[0] ?? 0));
    },
    recommend(rules, { height, weight, build = 'regular' }) {
      if (!Number.isFinite(height) || !Number.isFinite(weight) || height < 140 || height > 220 || weight < 35 || weight > 200) return null;
      if (!rules.length) return null;
      const contains = (range, value) => value >= range[0] && (range[1] === null || value <= range[1]);
      const matchesFor = (measurement, value) => {
        const known = rules.filter(rule => rule[measurement]);
        const matches = known.filter(rule => contains(rule[measurement], value));
        if (matches.length || !known.length) return { matches };
        const lowest = known.reduce((best, rule) => rule[measurement][0] < best[measurement][0] ? rule : best);
        const highest = known.reduce((best, rule) => (rule[measurement][1] ?? Infinity) >= (best[measurement][1] ?? Infinity) ? rule : best);
        // Clamp each measurement separately so a short height cannot override a higher weight bracket.
        if (value < lowest[measurement][0]) return { matches: [lowest], boundary: 'below' };
        if (highest[measurement][1] !== null && value > highest[measurement][1]) return { matches: [highest], boundary: 'above' };
        // In a gap between brackets, use the next bracket up instead of sizing down.
        const nextMinimum = Math.min(...known.filter(rule => rule[measurement][0] > value).map(rule => rule[measurement][0]));
        return { matches: known.filter(rule => rule[measurement][0] === nextMinimum) };
      };
      const heightMatch = matchesFor('height', height);
      const combinedMatches = heightMatch.matches.filter(rule => !rule.weight || contains(rule.weight, weight));
      let matches = combinedMatches;
      let weightFallback = false;
      if (!matches.length) {
        const weightMatch = matchesFor('weight', weight);
        // Guide rules are ordered from smaller to larger sizes. Use the measurement
        // requiring the larger size, then let carrure resolve overlaps in that bracket.
        weightFallback = weightMatch.matches.length > 0 && rules.indexOf(weightMatch.matches[0]) > rules.indexOf(heightMatch.matches[0]);
        const shared = heightMatch.matches.filter(rule => weightMatch.matches.includes(rule));
        matches = shared.length ? shared : weightFallback ? weightMatch.matches : heightMatch.matches;
      }
      if (!matches.length) return null;
      const heightFallback = !combinedMatches.length && !weightFallback;
      const result = rule => weightFallback ? { ...rule, weightFallback: true }
        : heightMatch.boundary ? { ...rule, heightBoundary: heightMatch.boundary }
        : heightFallback ? { ...rule, heightFallback: true } : rule;
      if (build === 'slim') return result(matches[0]);
      if (build === 'broad') return result(matches[matches.length - 1]);
      const distance = rule => {
        const midpoint = (rule.height[0] + (rule.height[1] ?? rule.height[0])) / 2;
        const heightDistance = weightFallback ? 0 : Math.abs(height - midpoint) / Math.max((rule.height[1] ?? rule.height[0] + 10) - rule.height[0], 1);
        const weightDistance = !heightFallback && rule.weight && rule.weight[1] !== null ? Math.abs(weight - (rule.weight[0] + rule.weight[1]) / 2) / Math.max(rule.weight[1] - rule.weight[0], 1) : 0;
        return heightDistance + weightDistance;
      };
      return result([...matches].sort((a, b) => distance(a) - distance(b))[0]);
    },
    variantForSize(product, size, selectedOptions) {
      // Preserve colour and every other selected option, including sold-out combinations.
      return product.variants.find(variant => normalize(variant.options[product.optionIndex]) === normalize(size)
        && variant.options.every((value, index) => index === product.optionIndex || value === selectedOptions[index]));
    }
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  if (typeof window === 'undefined' || customElements.get('rm-fit-calculator')) return;

  class RmFitCalculator extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (element, event, handler) => element?.addEventListener(event, handler, { signal: this.controller.signal });
      this.root = this.closest('[data-rm-product-page]');
      this.dialog = this.querySelector('dialog');
      if (!this.root || !this.dialog) return;
      this.product = JSON.parse(this.querySelector('[data-rm-fit-product]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-rm-fit-strings]').textContent);
      this.measurements = { height: 177, weight: 75 };
      this.units = 'metric';
      this.step = 0;
      this.openers = [...this.root.querySelectorAll('[data-rm-fit-open]')];
      this.openers.forEach(button => {
        button.hidden = this.product.sizes.length < 2;
        on(button, 'click', () => this.open(button));
      });
      on(this.querySelector('[data-rm-fit-close]'), 'click', () => this.dialog.close());
      on(this.querySelector('[data-rm-fit-back]'), 'click', () => this.showStep(this.step - 1));
      on(this.dialog, 'click', event => { if (event.target === this.dialog) this.dialog.close(); });
      on(this.dialog, 'close', () => {
        document.documentElement.classList.remove('rm-fit-open');
        this.opener?.focus({ preventScroll: true });
      });
      on(this.querySelector('[data-rm-measurements]'), 'submit', event => {
        event.preventDefault();
        if (this.validate()) this.showStep(1);
      });
      on(this.querySelector('[data-rm-build]'), 'submit', event => { event.preventDefault(); this.calculate(); });
      this.querySelectorAll('.rm-fit-units input').forEach(input => on(input, 'change', () => {
        this.units = input.value;
        this.renderMeasurements();
      }));
      this.querySelectorAll('[data-rm-measure]').forEach(field => {
        const name = field.dataset.rmMeasure;
        const number = field.querySelector('input[type=number]');
        const range = field.querySelector('input[type=range]');
        const update = event => {
          const value = event.target.valueAsNumber;
          if (!Number.isFinite(value)) { this.measurements[name] = NaN; return; }
          this.measurements[name] = this.units === 'metric' ? value : name === 'height' ? value * 2.54 : value / 2.2046226218;
          const other = event.target === range ? number : range;
          other.value = value;
          this.paintRange(range);
          this.querySelector('[data-rm-fit-error]').hidden = true;
        };
        on(number, 'input', update);
        on(range, 'input', update);
      });
      on(this.querySelector('[data-rm-fit-apply]'), 'click', () => this.apply());
      on(this.querySelector('[data-rm-fit-guide]'), 'click', () => this.openGuide());
      this.renderMeasurements();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
    }
    guide() { return this.root.querySelector('[data-rm-size-guide]'); }
    open(opener) {
      this.opener = opener;
      // Re-read on every opening so theme-editor edits and translated guides are reflected.
      const guide = this.guide();
      const text = guide ? guide.innerHTML.replace(/<br\s*\/?\s*>|<\/(?:p|li|tr|td|h[1-6])>/gi, '\n').replace(/<[^>]*>/g, '') : '';
      const decoded = document.createElement('textarea');
      decoded.innerHTML = text;
      this.rules = engine.parse(decoded.value, this.product.sizes);
      this.dialog.showModal();
      document.documentElement.classList.add('rm-fit-open');
      this.showStep(0);
    }
    showStep(step) {
      this.step = Math.max(0, Math.min(2, step));
      this.querySelectorAll('[data-rm-fit-step]').forEach(el => { el.hidden = Number(el.dataset.rmFitStep) !== this.step; });
      this.querySelectorAll('[data-rm-fit-title]').forEach(el => { el.hidden = Number(el.dataset.rmFitTitle) !== this.step; });
      this.querySelectorAll('.rm-fit-progress li').forEach((el, index) => {
        if (index === this.step) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
      });
      this.querySelector('[data-rm-fit-back]').hidden = this.step === 0;
      this.dialog.scrollTop = 0;
      this.querySelector('[data-rm-fit-heading]').focus({ preventScroll: true });
    }
    renderMeasurements() {
      this.querySelectorAll('[data-rm-measure]').forEach(field => {
        const name = field.dataset.rmMeasure;
        const metric = this.units === 'metric';
        const height = name === 'height';
        const unit = metric ? (height ? 'cm' : 'kg') : (height ? 'in' : 'lb');
        const min = metric ? (height ? 140 : 35) : (height ? 55.2 : 77.2);
        const max = metric ? (height ? 220 : 200) : (height ? 86.6 : 440.9);
        const converted = metric ? this.measurements[name] : height ? this.measurements[name] / 2.54 : this.measurements[name] * 2.2046226218;
        field.querySelectorAll('input').forEach(input => {
          input.min = min; input.max = max; input.step = metric ? '1' : '0.1';
          input.value = Number.isFinite(converted) ? Number(converted.toFixed(metric ? 0 : 1)) : '';
        });
        field.querySelector('[data-rm-unit]').textContent = unit;
        field.querySelector('[data-rm-min]').textContent = `${min} ${unit}`;
        field.querySelector('[data-rm-max]').textContent = `${max} ${unit}`;
        this.paintRange(field.querySelector('input[type=range]'));
      });
    }
    paintRange(range) { range.style.setProperty('--fill', `${Math.max(0, Math.min(100, (range.valueAsNumber - Number(range.min)) / (Number(range.max) - Number(range.min)) * 100))}%`); }
    validate() {
      const { height, weight } = this.measurements;
      const valid = Number.isFinite(height) && Number.isFinite(weight) && height >= 140 && height <= 220 && weight >= 35 && weight <= 200;
      const error = this.querySelector('[data-rm-fit-error]');
      error.hidden = valid;
      error.textContent = valid ? '' : this.strings.invalid;
      return valid;
    }
    selectedOptions() {
      const form = this.root.querySelector('form[data-product-form]');
      const current = this.product.variants.find(variant => String(variant.id) === form?.querySelector('[name=id]')?.value);
      return current?.options ?? [];
    }
    calculate() {
      const build = this.querySelector('.rm-fit-build input:checked').value;
      this.recommendation = engine.recommend(this.rules, { ...this.measurements, build });
      this.variant = this.recommendation ? engine.variantForSize(this.product, this.recommendation.size, this.selectedOptions()) : null;
      this.querySelector('[data-rm-fit-size]').textContent = this.recommendation?.size ?? '—';
      this.querySelector('[data-rm-fit-note]').textContent = this.recommendation
        ? (!this.variant?.available ? this.strings.unavailable : this.recommendation.weightFallback ? this.strings.weightFallback : this.recommendation.heightBoundary ? this.strings.heightBoundary : this.recommendation.heightFallback ? this.strings.heightFallback : this.recommendation.weight ? this.strings.matched : this.strings.heightOnly)
        : this.rules.length ? this.strings.noMatch : this.strings.noGuide;
      const apply = this.querySelector('[data-rm-fit-apply]');
      apply.disabled = !!this.recommendation && !this.variant?.available;
      apply.hidden = !this.recommendation;
      this.querySelector('[data-rm-fit-apply-text]').textContent = this.variant?.available ? this.strings.add : this.strings.unavailable;
      this.querySelector('[data-rm-fit-guide]').hidden = !this.guide();
      this.showStep(2);
    }
    async apply() {
      if (!this.variant?.available) return;
      const form = this.root.querySelector('form[data-product-form]');
      if (!form) return;
      const button = this.querySelector('[data-rm-fit-apply]');
      button.disabled = true;
      this.root.dispatchEvent(new CustomEvent('theme:change:variant', { detail: { variantId: this.variant.id } }));
      // Alpine updates the selected variant and submit button on its next tick.
      if (window.Alpine?.nextTick) await window.Alpine.nextTick();
      await new Promise(resolve => requestAnimationFrame(resolve));
      const submit = form.querySelector('.add-to-cart-btn');
      if (String(form.querySelector('[name=id]')?.value) !== String(this.variant.id) || !submit || submit.disabled) {
        button.disabled = false;
        return;
      }
      this.dialog.close();
      // Reuse the existing product form: quantity, selling plan, properties and cart drawer remain supported.
      form.requestSubmit(submit);
      button.disabled = false;
    }
    openGuide() {
      this.dialog.close();
      const guide = this.guide();
      const details = guide?.closest('details');
      if (details) details.open = true;
      (details ?? guide)?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    }
  }
  customElements.define('rm-fit-calculator', RmFitCalculator);

  function enhance(root) {
    if (root.rmPageController) return;
    const controller = new AbortController();
    root.rmPageController = controller;
    root.querySelectorAll('.product-block-description').forEach(description => {
      const preview = description.querySelector('.rm-description-preview');
      if (!preview) return;
      const full = description.querySelector('.rm-description-full');
      const source = preview.textContent.trim();
      const alreadyTruncated = /(?:\.{3}|…)$/.test(source);
      const words = source.replace(/(?:\.{3}|…)$/, '').split(/\s+/);
      let lastWidth = 0;
      const fitPreview = () => {
        if (!preview.getClientRects().length || full?.getClientRects().length) return;
        const styles = getComputedStyle(description);
        const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.2;
        const maxHeight = lineHeight * 3 + 1;
        const showWords = count => {
          preview.textContent = words.slice(0, count).join(' ') + (count < words.length || alreadyTruncated ? '...' : '');
        };
        // Include the inline toggle in the measurement, so it always remains visible
        // after the ellipsis within the same three-line preview.
        let low = 1;
        let high = words.length;
        while (low < high) {
          const count = Math.ceil((low + high) / 2);
          showWords(count);
          if (description.clientHeight <= maxHeight) low = count;
          else high = count - 1;
        }
        showWords(low);
      };
      const resize = new ResizeObserver(() => {
        if (description.clientWidth === lastWidth) return;
        lastWidth = description.clientWidth;
        fitPreview();
      });
      resize.observe(description);
      const visibility = new MutationObserver(() => requestAnimationFrame(() => {
        if (!controller.signal.aborted) fitPreview();
      }));
      visibility.observe(preview, { attributes: true, attributeFilter: ['style'] });
      if (full) visibility.observe(full, { attributes: true, attributeFilter: ['style'] });
      document.fonts?.ready.then(() => { if (!controller.signal.aborted) fitPreview(); });
      controller.signal.addEventListener('abort', () => { resize.disconnect(); visibility.disconnect(); });
      fitPreview();
    });
    root.addEventListener('click', event => {
      const link = event.target.closest('[data-rm-reviews]');
      if (!link) return;
      const targets = [...document.querySelectorAll('[id="judgeme_product_reviews"], .jdgm-review-widget')];
      const target = targets.find(element => element.getClientRects().length && !root.contains(element))
        ?? targets.find(element => element.getClientRects().length) ?? targets[0];
      if (!target) return;
      event.preventDefault();
      // Some existing custom-Liquid sections hide this wrapper until their own
      // observer finds reviews. Reveal the requested section even if that observer failed.
      target.closest('.jdgm-review-section-wrapper')?.classList.add('has-reviews');
      const details = target.closest('details');
      if (details) details.open = true;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    }, { signal: controller.signal });
    const buy = root.querySelector('.product-block-buy-buttons');
    const klarnaSlot = buy?.querySelector('[data-rm-klarna]');
    if (klarnaSlot) {
      const placeKlarna = () => {
        const block = root.querySelector('klarna-placement')?.closest('.shopify-app-block')
          ?? root.querySelector('.shopify-app-block[id*="klarna_on_site_messaging"]');
        if (block && block.parentElement !== klarnaSlot) klarnaSlot.append(block);
      };
      const observer = new MutationObserver(placeKlarna);
      observer.observe(root, { childList: true, subtree: true });
      placeKlarna();
      controller.signal.addEventListener('abort', () => observer.disconnect());
    }
    const slot = buy?.querySelector('[data-rm-delivery]');
    if (slot) {
      const placeDelivery = () => {
        const incoming = [...buy.querySelectorAll('.bb-tag-root')].filter(widget => widget.parentElement !== slot);
        if (!incoming.length) return;
        // Bigblue replaces its original placement on variant changes. Keep the new instance
        // and discard the old moved instance, so each update still shows exactly one widget.
        const latest = incoming[incoming.length - 1];
        slot.replaceChildren(latest);
        incoming.slice(0, -1).forEach(widget => widget.remove());
      };
      const observer = new MutationObserver(placeDelivery);
      observer.observe(buy, { childList: true, subtree: true });
      placeDelivery();
      controller.signal.addEventListener('abort', () => observer.disconnect());
    }
  }
  const init = container => container.querySelectorAll('[data-rm-product-page]').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-rm-product-page]').forEach(root => root.rmPageController?.abort()));
})();
